<template>
    <div class="slider-image-upload">
        <div class="custom-upload-wrapper">
            <!-- Upload Box -->
            <div
                class="upload-box d-flex flex-column justify-content-center align-items-center"
                @click="triggerFileInput"
                @dragover.prevent="dragOver = true"
                @dragleave.prevent="dragOver = false"
                @drop.prevent="handleDrop"
                :class="{ 'drag-over': dragOver }"
            >
                <template v-if="!imageUrl">
                    <!-- Icon -->
                    <div class="mb-2 imgUpload ppv2-h-[35px] ppv2-w-[35px]" />

                    <div class="poppinsSemiBold ppv2-text-center ppv2-text-black" :style="{ fontSize: normalizeFontSize(13) }">
                        <span class="ppv2-text-[#0075FF] poppinsSemiBold">{{ $t('experience.step2.importFile1') }}</span>
                        {{ $t('experience.step2.importFile2') }}<br />
                    </div>
                    <span class="poppinsRegular ppv2-text-[#a3a3a3]" :style="{ fontSize: normalizeFontSize(12) }">
                        {{ $t('experience.step2.requiredTypeFile') }}
                    </span>
                    <span class="poppinsRegular ppv2-text-[#a3a3a3]" :style="{ fontSize: normalizeFontSize(12) }">
                        {{ recommendedSize }}
                    </span>
                </template>

                <template v-else>
                    <img :src="imageUrl" alt="Preview" class="preview-image" />

                    <!-- Clear Button (Top Right) -->
                    <div
                        v-if="displayRemoveButton"
                        class="ppv2-cursor-pointer ppv2-absolute ppv2-right-2 ppv2-top-2 ppv2-z-50 ppv2-h-[25px] ppv2-w-[25px] hover:ppv2-bg-gray-100 dark:hover:ppv2-bg-gray-700 ppv2-rounded-full ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-justify-center"
                        @click.stop="removeImage"
                    >
                        <X color="#505D92" :size="15" />
                    </div>

                    <!-- Actions Overlay (Only Crop and Refresh) -->
                    <div
                        class="ppv2-absolute ppv2-inset-0 ppv2-bg-black/40 ppv2-opacity-0 hover:ppv2-opacity-100 ppv2-transition-opacity ppv2-flex ppv2-items-center ppv2-justify-center ppv2-rounded-xl"
                        :style="{ gap: normalizeSize(16, true) }"
                    >
                        <BaseButton
                            variant="primary"
                            customClass="!ppv2-px-3 !ppv2-py-2"
                            @click.stop="openCropper"
                            :title="$t('common.crop')"
                        >
                            <Crop :size="20" />
                        </BaseButton>
                        <BaseButton
                            variant="primary"
                            customClass="!ppv2-px-3 !ppv2-py-2"
                            @click.stop="changeImage"
                            :title="$t('common.change')"
                        >
                            <RefreshCw :size="20" />
                        </BaseButton>
                    </div>
                </template>
            </div>

            <input ref="fileInput" type="file" accept="image/*" class="ppv2-hidden" @change="handleFileSelect" />
        </div>

        <!-- CustomDialog for Cropping (matching CustomInputUpload style) -->
        <CustomDialog
            v-model="showCropper"
            :defaultTitle="$t('website_settings.general.position_image')"
            width="800px"
            @cancel="showCropper = false"
        >
            <template #content>
                <div class="ppv2-flex ppv2-gap-2 ppv2-flex-wrap ppv2-mb-4">
                    <BaseButton
                        type="button"
                        v-for="option in aspectOptions"
                        :key="option.label"
                        @click="selectAspectRatio(option)"
                        customClass="py-1 px-3 ppv2-flex ppv2-items-center"
                        :customHeight="40"
                        :variant="selectedAspect === option.value ? 'primary' : 'outline'"
                    >
                        <component :is="option.icon" :size="16" class="ppv2-mr-2" />
                        {{ $t(option.label) }}
                    </BaseButton>
                </div>

                <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2" :style="{ gap: normalizeSize(16, true) }">
                    <!-- Cropper -->
                    <cropper
                        ref="cropper"
                        :src="tempImageUrl"
                        class="ppv2-w-full ppv2-h-[400px] ppv2-border ppv2-border-gray-200 ppv2-rounded-md"
                        :stencil-props="{ aspectRatio: selectedAspect }"
                        :auto-zoom="true"
                        @change="onCropChange"
                    />

                    <!-- Preview -->
                    <div class="ppv2-border ppv2-border-gray-200 ppv2-rounded-md ppv2-p-2 ppv2-bg-gray-50">
                        <h3 class="ppv2-text-sm poppinsMedium ppv2-mb-2">{{ $t('website_settings.general.preview') }}</h3>
                        <div
                            class="ppv2-w-full ppv2-h-[250px] ppv2-flex ppv2-items-center ppv2-justify-center ppv2-bg-white ppv2-rounded"
                        >
                            <img
                                v-if="croppedPreview"
                                :src="croppedPreview"
                                class="ppv2-max-w-full ppv2-max-h-full ppv2-object-contain"
                            />
                            <span v-else class="ppv2-text-gray-400">{{ $t('website_settings.general.no_preview') }}</span>
                        </div>
                    </div>
                </div>
            </template>
            <template #actions>
                <BaseButton variant="primary" @click="confirmCrop">
                    {{ $t('common.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import normalizeSize from '../../../../../utils/normalizeSize'
import BaseLabel from '../../../../Shared/BaseLabel.vue'
import CustomDialog from '../../../../Shared/CustomDialog.vue'
import BaseButton from '../../../../Shared/BaseButton.vue'
import { X, Crop, RefreshCw, Trash2, Tv, Monitor, Square, Maximize } from 'lucide-vue'
import normalizeFontSize from '../../../../../utils/normalizeFontSize'

export default {
    name: 'SliderImageUpload',
    components: {
        Cropper,
        BaseLabel,
        CustomDialog,
        BaseButton,
        Crop,
        RefreshCw,
        Trash2,
        Tv,
        Monitor,
        Square,
        Maximize,
        X,
    },
    props: {
        value: {
            type: Object,
            default: () => ({ url: null, file: null }),
        },
        recommendedSize: {
            type: String,
            default: '1920×1080px',
        },
        displayRemoveButton: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            imageUrl: null,
            tempImageUrl: null,
            showCropper: false,
            selectedAspect: 16 / 9,
            croppedPreview: null,
            dragOver: false,
            aspectOptions: [
                { label: 'website_settings.general.aspect_16_9', value: 16 / 9, icon: 'Tv' },
                { label: 'website_settings.general.aspect_4_3', value: 4 / 3, icon: 'Monitor' },
                { label: 'website_settings.general.aspect_square', value: 1, icon: 'Square' },
                { label: 'website_settings.general.aspect_free', value: null, icon: 'Maximize' },
            ],
            originalFile: null,
        }
    },
    watch: {
        value: {
            immediate: true,
            handler(newVal) {
                if (newVal && newVal.url) {
                    this.imageUrl = newVal.url
                } else {
                    this.imageUrl = null
                }
            },
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        triggerFileInput() {
            if (!this.imageUrl) {
                this.$refs.fileInput.click()
            }
        },
        handleFileSelect(e) {
            const file = e.target.files[0]
            if (file) this.processFile(file)
        },
        handleDrop(e) {
            this.dragOver = false
            const file = e.dataTransfer.files[0]
            if (file) this.processFile(file)
        },
        processFile(file) {
            if (file.size > 1024 * 1024 * 5) {
                // 5MB limit
                this.$toast.error(this.$t('common.file_too_large'))
                return
            }
            this.originalFile = file
            const reader = new FileReader()
            reader.onload = (e) => {
                this.tempImageUrl = e.target.result
                // this.imageUrl = e.target.result // Don't set main image yet, wait for crop
                this.showCropper = true
            }
            reader.readAsDataURL(file)
        },
        changeImage() {
            this.$refs.fileInput.click()
        },
        removeImage() {
            this.imageUrl = null
            this.tempImageUrl = null
            this.croppedPreview = null
            this.$emit('input', { url: null, file: null })
            this.$refs.fileInput.value = ''
        },
        openCropper() {
            this.tempImageUrl = this.imageUrl
            this.showCropper = true
        },
        selectAspectRatio(option) {
            this.selectedAspect = option.value
        },
        onCropChange({ canvas }) {
            if (canvas) {
                this.croppedPreview = canvas.toDataURL()
            }
        },
        confirmCrop() {
            const canvas = this.$refs.cropper.getResult().canvas
            if (!canvas) return

            canvas.toBlob((blob) => {
                const file = new File([blob], this.originalFile?.name || 'slider-image.webp', {
                    type: 'image/webp',
                })
                const url = URL.createObjectURL(file)

                this.imageUrl = url
                this.$emit('input', { url, file })
                this.showCropper = false
            }, 'image/webp')
        },
    },
    computed: {
        isDark() {
            return this.theme.mode === 'dark'
        },
    },
}
</script>

<style scoped>
.custom-upload-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 20px;
    opacity: 0.7;
    transition: opacity 0.3s ease;
}

.custom-upload-wrapper:focus-within,
.custom-upload-wrapper:hover {
    opacity: 1;
}

.upload-box {
    background: rgba(244, 250, 254, 0.05);
    border: 1.5px dashed #9c9af880;
    border-radius: 12px;
    color: #bcbcbc;
    padding: 1rem;
    text-align: center;
    cursor: pointer;
    position: relative;
    transition: all 0.3s ease;
    height: 180px; /* Specific to slider */
    overflow: hidden;
}

.upload-box.drag-over {
    border-color: #9c9af8b2;
    background: rgba(156, 154, 248, 0.08);
}

.preview-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 10px;
}
</style>
